import _ from 'lodash';
import { Record as record, List, Set as set } from 'immutable';

import config from '../config';
import Station from './Station';
import trainTime from '../lib/TrainTime';
import Timeband, { DEFAULT_WINDOW_LENGTH } from './Timeband';
import { sum } from '../helpers/Immutable';
import PassengerRailcard from './PassengerRailcard';
import Favourite from './Favourite';
import FareOnlySearchPreferences from './FareOnlySearchPreferences';
import OrderFare from './order/Fare';
import { construct, resolve, resolveAll } from '../helpers/LinkMapper';
import { TicketTypes, JourneyDirections, ServicesPerLayout } from '../constants/AppConstants';
// @note import deserialize as deserialize - workaround an issue with the unit tests & rewire
import { serialize, deserialize as deserialize, serializers, deserializers } from '../helpers/SearchUrls';
import SearchMixin from './SearchMixin';
import PromoCode from './PromoCode';
import moment from 'moment';

function _getDefaultOutboundDateTime (smartcard) {
    const offset = smartcard ? 'minSmartcardOffset' : 'minTodOffset';
    const todDuration = trainTime.duration(config.get(offset));
    const outbound = trainTime().add(todDuration);
    const roundedUp = Math.ceil(outbound.minute() / 15) * 15;

    return outbound.clone().minute(roundedUp).second(0);
}

function _getDefaultReturnDateTime (outboundDateTime) {
    const roundedUp = Math.ceil(outboundDateTime.minute() / 15) * 15;
    return outboundDateTime.add(2, 'hours').minute(roundedUp).second(0);
}

const defaults = {
    id: null,
    origin: new Station(),
    destination: new Station(),
    via: new Station(),
    avoid: new Station(),
    ticketType: TicketTypes.SINGLE,
    outboundDateTime: new Timeband({datetime: _getDefaultOutboundDateTime()}),
    returnDateTime: new Timeband({datetime: _getDefaultReturnDateTime(_getDefaultOutboundDateTime())}),
    adults: 1,
    children: 0,
    numJourneys: ServicesPerLayout[config.get('journeyPlannerDefaultLayout')],
    railcards: new List(),
    submitted: false,
    smartcard: false,
    groupSave: false,
    tocSpecific: false,
    showCheapest: config.get('journeyPlannerDefaultCheapestSearch'),
    targetLayout: config.get('journeyPlannerDefaultLayout'),
    realTime: false,
    departNow: false,
    searchPreferences: new FareOnlySearchPreferences(),
    showAdditionalRoutes: false,
    promoCode: new PromoCode(),
    orderId: null,
    isAmending: false,
    email: null
};

class Search extends record(defaults) {

    serialize () {
        const params = serialize({
            origin: serializers.station,
            destination: serializers.station,
            outboundDateTime: serializers.timeBand,
            returnDateTime: serializers.timeBand,
            railcards: serializers.railcards,
            adults: serializers.identity,
            children: serializers.identity,
            numJourneys: serializers.identity,
            targetLayout: serializers.identity,
            promoCode: serializers.promoCode,
            email: serializers.identity,
        }, this);

        const querySchema = {
            via: serializers.station,
            avoid: serializers.station,
            tocSpecific: serializers.boolean,
            showCheapest: serializers.boolean,
            realTime: serializers.boolean,
            departNow: serializers.boolean,
            searchPreferences: (preferences) => preferences.toString(),
            showAdditionalRoutes: serializers.boolean,
            promoCode: serializers.promoCode,
            email: serializers.identity,
        };

        const query = serialize(querySchema, this);

        return {
            params,
            query
        };
    }

    toFareOnlyJSON () {
        return serialize({
            origin: station => station.nlc,
            destination: station => station.nlc,
            via: serializers.station,
            avoid: serializers.station,
            outward: () => serializers.date(this.outboundDateTime.datetime),
            return: () => serializers.date(this.returnDateTime.datetime),
            adults: serializers.identity,
            children: serializers.identity,
            searchPreferences: searchPreferences => searchPreferences.toJSON(),
            promoCode: (promoCode) => promoCode ? promoCode.promoCode : null,
            smartCard: () => serializers.identity(this.smartcard),
            railcards: () => serializers.railcardCodes(this.railcards)
        }, this);
    }

    setTargetLayout (layout) {
        const numJourneys = ServicesPerLayout[layout];
        return this.set('targetLayout', layout)
            .set('numJourneys', numJourneys);
    }

    getDefaultOutboundDateTime () {
        return _getDefaultOutboundDateTime(this.smartcard);
    }

    getDefaultReturnDateTime () {
        return _getDefaultReturnDateTime(this.getDefaultOutboundDateTime());
    }

    setTicketType (value) {
        return this.set('ticketType', value);
    }

    getTimebandForDirection (direction) {
        return direction === JourneyDirections.INWARD ? this.returnDateTime : this.outboundDateTime;
    }

    validateDatesBeforeMax () {
        const beforeMax = (date) => trainTime().add(config.get('maxOutboundDays'), 'days').isSameOrAfter(date);

        if (this.isSingle() || this.isOpenReturn()) {
            return beforeMax(this.getOutboundDateTime());
        } else {
            return beforeMax(this.getOutboundDateTime()) && beforeMax(this.getReturnDateTime());
        }
    }

    validateDepartBeforeArrive () {
        if (this.isReturn()) {
            return this.getReturnDateTime().isAfter(this.getOutboundDateTime());
        } else {
            return true;
        }
    }

    validateDatesComplete () {
        if (this.isSingle() || this.isOpenReturn()) {
            return this.getOutboundDateTime().isValid();
        } else {
            return this.getOutboundDateTime().isValid() && this.getReturnDateTime().isValid();
        }
    }

    validateDatesInFuture () {
        const isTransPennineRover = this.email && this.origin.nlc === 'Z031' && this.destination.nlc === 'Z032';

        if (isTransPennineRover) {
            // In order to find TransPennine Rover journeys the the time is fixed to 11:30am
            // so it allow for searches to be made in the past
            return true;
        }

        if (this.isSingle() || this.isOpenReturn()) {
            return this.departNow || this.getOutboundDateTime().isAfter();
        } else {
            return (this.departNow || this.getOutboundDateTime().isAfter()) && this.getReturnDateTime().isAfter();
        }
    }

    validateSmartcardPeriodReturn () {
        if (this.smartcard && this.isReturn()) {
            const outboundStart = this.getOutboundDateTime().clone().startOf('day');
            const returnStart = this.getReturnDateTime().clone().startOf('day');
            return outboundStart.isSame(returnStart);
        } else {
            return true;
        }
    }

    validateAtLeastOnePassenger () {
        return this.adults + this.children >= 1;
    }

    validateLessThanNinePassengers () {
        return this.adults + this.children <= 9;
    }

    validateRailcardsQuantity () {
        let passengers = this.adults + this.children;
        let railcards = sum(this.railcards, 'quantity');
        return railcards <= passengers;
    }

    getTotalPassengercount () {
        return this.adults + this.children;
    }

    isValid () {
        return this.getValidationErrors().isEmpty();
    }

    hasPromoCode() {
        return !!this.getPromoCode();
    }

    getPromoCode () {
        return this.promoCode && this.promoCode.promoCode;
    }

    getValidationErrors () {
        const tests = set([
            'validateStationsComplete',
            'validateStationsNotEqual',
            'validateDatesComplete',
            'validateDepartBeforeArrive',
            'validateAtLeastOnePassenger',
            'validateLessThanNinePassengers',
            'validateRailcardsQuantity',
            'validateSmartcardPeriodReturn',
            'validateDatesBeforeMax'
        ]);

        return tests.filterNot((test) => this[test]());
    }

    refreshDates (smartcard) {
        const currentOutwardTime = this.getOutboundDateTime();
        if (smartcard && smartcard.isAwaitingProduct()) {
            const minOutwardTime =
                trainTime().addWorkingDays(config.get('minAwaitingSmartcardProductDays'))
                    .hour(currentOutwardTime.hour())
                    .minute(currentOutwardTime.minute());
            if (minOutwardTime.isAfter(currentOutwardTime)) {
                return this.setOutboundDateTime(minOutwardTime);
            }
        }
        if (trainTime().isAfter(currentOutwardTime) || trainTime().isAfter(this.getReturnDateTime())) {
            return this
                .setOutboundDateTime(this.getDefaultOutboundDateTime())
                .setReturnDateTime(this.getDefaultReturnDateTime());
        }
        if (currentOutwardTime.isAfter(this.getReturnDateTime())) {
            return this.setReturnDateTime(_getDefaultReturnDateTime(currentOutwardTime));
        }

        return this;
    }

    refreshAmending () {
        return this.set('isAmending', false);
    }

    slideAfterChange () {
        if (this.getOutboundDateTime().isAfter(this.getReturnDateTime())) {
            return this.setReturnDateTime(_getDefaultReturnDateTime(this.getOutboundDateTime()));
        } else if (this.getOutboundDateTime().isBefore(trainTime())) {
            return this.setOutboundDateTime(_getDefaultOutboundDateTime(this.smartcard));
        }

        return this;
    }

    setOutwardTimeNow () {
        return this.setIn(['outboundDateTime', 'arriveDepart'], 'Depart')
            .setIn(['outboundDateTime', 'datetime'], trainTime().add(1, 'minutes'));
    }

    earlier (direction, services) {
        const dateTimeKey = direction === JourneyDirections.OUTWARD ? 'outboundDateTime' : 'returnDateTime';

        let updatedSearch;
        if (services.size > 0) {
            const updatedDateTime = services.first().getArrivalDateTime(false).add(1, 'minute');

            updatedSearch = this
                .setIn([dateTimeKey, 'arriveDepart'], 'Arrive')
                .setIn([dateTimeKey, 'datetime'], updatedDateTime);
        } else {
            updatedSearch = this.set(dateTimeKey, this.get(dateTimeKey).getEarlier());
        }

        if (direction === JourneyDirections.INWARD && !updatedSearch.returnDateTime.getEnd().isAfter(updatedSearch.outboundDateTime.getEnd())) {
            updatedSearch = updatedSearch.set('outboundDateTime', updatedSearch.get(dateTimeKey).getEarlier());
        }

        return updatedSearch;
    }

    later (direction, services) {
        const dateTimeKey = direction === JourneyDirections.OUTWARD ? 'outboundDateTime' : 'returnDateTime';

        if (services.size > 0) {
            return this
                .setIn([dateTimeKey, 'arriveDepart'], 'Depart')
                .setIn([dateTimeKey, 'datetime'], services.last().getDepartureDateTime(false));

        } else {
            return this.set(dateTimeKey, this.get(dateTimeKey).getLater());
        }
    }

    isEarlierSearchDisabled({ direction, services, trip }) {
        try {
            const { search } = trip;
            const dateTimeKey = direction === JourneyDirections.OUTWARD ? 'outboundDateTime' : 'returnDateTime';

            const { rangeStart, rangeEnd } = search[dateTimeKey].toRange({ truncateStart: trainTime() });

            return services.size === 1 &&
                moment(rangeStart).isBefore(moment()) &&
                moment(rangeEnd).diff(moment(rangeStart), 'hours') < DEFAULT_WINDOW_LENGTH;
        } catch (error) {
            console.error(error);
            return true;
        }
    }

    setOrigin (station) {
        return this.set('origin', station);
    }

    setPromoCode(promo) {
        return this.set('promoCode', promo);
    }

    setDestination (station) {
        return this.set('destination', station);
    }

    addReturn () {
        return this.setTicketType(TicketTypes.RETURN).refreshDates();
    }

    isSingle () {
        return this.get('ticketType') === TicketTypes.SINGLE;
    }

    isReturn () {
        return this.get('ticketType') === TicketTypes.RETURN;
    }

    isReturnLondonTravelcard () {
        return this.destination.isLondonStation() && this.destination.isTravelcard();
    }

    isOpenReturn () {
        return this.get('ticketType') === TicketTypes.OPEN_RETURN;
    }

    getOutboundDateTime () {
        return _.result(this.getIn(['outboundDateTime', 'datetime']), 'clone');
    }

    setOutboundDateTime (datetime = this.getDefaultOutboundDateTime()) {
        return this.setIn(['outboundDateTime', 'datetime'], datetime);
    }

    getReturnDateTime () {
        return _.result(this.getIn(['returnDateTime', 'datetime']), 'clone');
    }

    setReturnDateTime (datetime = this.getDefaultReturnDateTime()) {
        return this.setIn(['returnDateTime', 'datetime'], datetime);
    }

    setOutboundDateTimeWindow (hours) {
        return this.setIn(['outboundDateTime', 'windowLength'], hours);
    }

    setReturnDateTimeWindow (hours) {
        return this.setIn(['returnDateTime', 'windowLength'], hours);
    }

    getMinDate (direction) {
        return (this._isReturnDirection(direction)) ? this.getOutboundDateTime() : trainTime();
    }

    getMaxDate (direction) {
        return (this._isReturnDirection(direction) && this.smartcard) ?
            this.getOutboundDateTime() : trainTime().add(config.get('maxOutboundDays'), 'days');
    }

    _isReturnDirection (direction) {
        return (direction === JourneyDirections.INWARD);
    }

    setNumberOfJourneys (num) {
        return this.set('numJourneys', num);
    }

    getRouteOptions () {
        const options = config.get('journeySelectorOptions');
        const additionalOptions = config.get('journeySelectorAdditionalOptions');
        if (this.showAdditionalRoutes) {
            return options.concat(additionalOptions);
        }
        return options;
    }

    matchesRouteOptions() {
        // where options is a List of Lists which contain a pair of CRSs
        // (see journeySelectorOptions in config)
        return this.getRouteOptions().some(option =>
            option.get(0) === this.origin.crs && option.get(1) === this.destination.crs);
    }

    static fromAmendRequest(amendRequest) {
        const { trip, fares, order } = amendRequest;
        // get the first fare that is actually a fare, not a
        // plusbus etc.
        const fare = fares.keySeq().toList().filter((x) => !x.isPlusBus()).first();
        const adults = amendRequest.getAdults(fare);
        const children = amendRequest.getChildren(fare);
        const railcards = amendRequest.getAllValidRailcards({
            passengers: adults + children
        });
        const outwardJourney = trip.getOutwardJourney();
        const returnJourney = trip.getReturnJourney();

        const outboundDateTime = new Timeband({
            datetime: outwardJourney.getDepartureTime(),
            windowLength: 8
        });
        const returnDateTime = new Timeband({
            datetime: returnJourney ? returnJourney.getDepartureTime() : outwardJourney.getArrivalTime(),
            windowLength: 8
        });

        const hasPromoTickets = amendRequest.getAmendedTickets().some(ticket => ticket.hasPromoDiscount());

        return Search.fromJSON(Object.assign(
            defaults,
            {
                outboundDateTime,
                returnDateTime,
                railcards: railcards,
                origin: trip.getFirstOrigin(),
                destination: trip.getDestination(),
                adults,
                children,
                ticketType: amendRequest.hasReturnFare() ? TicketTypes.RETURN : TicketTypes.SINGLE,
                numJourneys: 8,
                promoCode: hasPromoTickets ? order.getPromoCode() : new PromoCode(),
                orderId: order.id,
                isAmending: true
            }));
       }

    static fromJSON (json = {}) {
        return construct(Search, json, {
            origin: resolve.as(Station),
            destination: resolve.as(Station),
            via: resolve.as(Station),
            avoid: resolve.as(Station),
            ticketType: resolve.with(String),
            outboundDateTime: resolve.as(Timeband),
            returnDateTime: resolve.as(Timeband),
            adults: resolve.with(Number),
            numJourneys: resolve.with(Number),
            children: resolve.with(Number),
            railcards: resolveAll.as(PassengerRailcard),
            submitted: resolve.with(Boolean),
            smartcard: resolve.with(Boolean),
            tocSpecific: resolve.with(Boolean),
            showCheapest: resolve.with(Boolean),
            targetLayout: resolve.with(String),
            realTime: resolve.with(Boolean),
            departNow: resolve.with(Boolean),
            searchPreferences: resolve.as(FareOnlySearchPreferences),
            showAdditionalRoutes: resolve.with(Boolean),
            promoCode: resolve.as(PromoCode),
            orderId: resolve.with(Number),
            email: resolve.with(String)
        });
    }

    /**
     * Return a promise that resolves to a new Search, from the url params
     */
    static deserialize (params, query) {
        const source = Object.assign({}, query, params);
        const schema = {
            origin: deserializers.station,
            destination: deserializers.station,
            via: deserializers.station,
            avoid: deserializers.station,
            adults: deserializers.int,
            children: deserializers.int,
            numJourneys: (value, json, name) => {
                if (value) {
                    return {[name]: value};
                } else if (json.targetLayout) {
                    return {[name]: ServicesPerLayout[json.targetLayout]};
                }
                return ServicesPerLayout[config.get('journeyPlannerDefaultLayout')];
            },
            railcards: deserializers.railcards,
            outboundDateTime: deserializers.timeBand,
            returnDateTime: deserializers.timeBand,
            tocSpecific: deserializers.boolean,
            showCheapest: deserializers.boolean,
            targetLayout: (value, json, name) => {
                if (value) {
                    return {[name]: value};
                }
                return {[name]: config.get('journeyPlannerDefaultLayout')};
            },
            realTime: deserializers.boolean,
            departNow: deserializers.boolean,
            searchPreferences: (value, data, name) => {
                return { [name]: FareOnlySearchPreferences.fromString(value) };
            },
            showAdditionalRoutes: deserializers.boolean,
            promoCode: deserializers.promoCode,
            email: deserializers.identity
        };

        return deserialize(schema, source).then((deserialized) => {
            return new Search(deserialized);
        });
    }

    static fromFare (fare, date = null) {
        return Search.fromFavourite(Favourite.fromFare(fare), date);
    }

    /**
     * @param {Favourite} favourite
     * @param {trainTime} date
     * @return {Search}
     */
    static fromFavourite (favourite, date = null) {
        if (!date) {
            date = _getDefaultOutboundDateTime();
        }

        const railcards = favourite.getPassengerRailcards();

        //Set to midday so offpeak and anytime are valid
        const outboundDate = date.clone().set({hour: 12, minute: 0});
        const ticketMultiplier = favourite.ticketType.ticketMultiplier();

        let search = new Search({
            origin: favourite.origin,
            destination: favourite.destination,
            adults: favourite.adults / ticketMultiplier,
            children: favourite.children / ticketMultiplier,
            ticketType: favourite.isReturn() ? TicketTypes.RETURN : TicketTypes.SINGLE,
            railcards,
            searchPreferences: FareOnlySearchPreferences.fromFavourite(favourite)
        }).setIn(['outboundDateTime', 'datetime'], outboundDate).refreshDates();

        if (favourite.isReturn()) {
            search = search.setIn(['returnDateTime', 'datetime'], outboundDate.clone().add(30, 'm')).refreshDates();
        }

        return search;
    }

    static fromReorderable (reorderable, date = null) {
        if (reorderable instanceof OrderFare) {
            return Search.fromFare(reorderable, date);
        } else if (reorderable instanceof Favourite) {
            return Search.fromFavourite(reorderable, date);
        }

        return null;
    }

    getJsonForJourneyNotification () {
        const json = {
            origin: this.origin.nlc,
            destination: this.destination.nlc,
            startDate: this.getOutboundDateTime().format('YYYY-MM-DD HH:mm:ss'),
            adult: this.adults > 0,
            child: this.children > 0
        };
        return this.isReturn() ? Object.assign({}, json, {
            endDate: this.getReturnDateTime().format('YYYY-MM-DD HH:mm:ss'),
        }) : json;
    }
}

Object.assign(Search.prototype, SearchMixin);

export default Search;



// WEBPACK FOOTER //
// ./src/records/Search.js